import type { Table } from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

import {
  pageSizeOptions,
  setStoredPageSize,
} from '~shared/helpers/page-size-preference';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

export function EnrollioTableRowsPerPage<TData>({
  table,
  label = 'Rows per page',
}: {
  table: Table<TData>;
  label?: string;
}) {
  const pageSize = table.getState().pagination.pageSize;
  return (
    <div className='flex items-center gap-2'>
      <span className='text-sm font-medium'>{label}</span>
      <Select
        value={String(pageSize)}
        onValueChange={(value) => {
          const next = Number(value);
          setStoredPageSize(next);
          table.setPageSize(next);
        }}
      >
        <SelectTrigger className='h-8 w-[72px]' aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent side='top'>
          {pageSizeOptions(pageSize).map((size) => (
            <SelectItem key={size} value={String(size)}>
              {size}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function PageSummary<TData>({ table }: { table: Table<TData> }) {
  return (
    <span className='text-sm text-muted-foreground'>
      Page {table.getState().pagination.pageIndex + 1} of{' '}
      {Math.max(1, table.getPageCount())}
    </span>
  );
}

export function EnrollioTablePaginationCompact<TData>({
  table,
  showRowsPerPage = true,
  className,
}: {
  table: Table<TData>;
  showRowsPerPage?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 pt-4 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2 sm:justify-start'>
        <PageSummary table={table} />
        {showRowsPerPage ? <EnrollioTableRowsPerPage table={table} /> : null}
      </div>
      <div className='flex gap-2'>
        <Button
          variant='outline'
          size='sm'
          disabled={!table.getCanPreviousPage()}
          onClick={() => {
            table.previousPage();
          }}
        >
          Previous
        </Button>
        <Button
          variant='outline'
          size='sm'
          disabled={!table.getCanNextPage()}
          onClick={() => {
            table.nextPage();
          }}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

export function EnrollioTablePaginationFull<TData>({
  table,
  showRowsPerPage = true,
  showSelection = false,
  className,
}: {
  table: Table<TData>;
  showRowsPerPage?: boolean;
  showSelection?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 pt-4 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2 sm:justify-start'>
        {showSelection ? (
          <span className='text-sm text-muted-foreground'>
            {table.getFilteredSelectedRowModel().rows.length} of{' '}
            {table.getFilteredRowModel().rows.length} selected
          </span>
        ) : null}
        {showRowsPerPage ? <EnrollioTableRowsPerPage table={table} /> : null}
      </div>
      <div className='flex items-center justify-between gap-3 sm:justify-end'>
        <PageSummary table={table} />
        <div className='flex gap-1'>
          <Button
            variant='outline'
            size='icon'
            className='size-8'
            aria-label='Go to first page'
            disabled={!table.getCanPreviousPage()}
            onClick={() => {
              table.setPageIndex(0);
            }}
          >
            <ChevronsLeft />
          </Button>
          <Button
            variant='outline'
            size='icon'
            className='size-8'
            aria-label='Go to previous page'
            disabled={!table.getCanPreviousPage()}
            onClick={() => {
              table.previousPage();
            }}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant='outline'
            size='icon'
            className='size-8'
            aria-label='Go to next page'
            disabled={!table.getCanNextPage()}
            onClick={() => {
              table.nextPage();
            }}
          >
            <ChevronRight />
          </Button>
          <Button
            variant='outline'
            size='icon'
            className='size-8'
            aria-label='Go to last page'
            disabled={!table.getCanNextPage()}
            onClick={() => {
              table.setPageIndex(Math.max(0, table.getPageCount() - 1));
            }}
          >
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
